import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { moduleDisplayName } from '../utils/moduleNames';
import axios from 'axios';
import { formatCurrency } from '../utils/format';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Per-module marketing detail. Backend's modules_collection only has
// short description; this page wants longer copy + features + use
// cases that are pure marketing content. The copy lives in the
// `moduleInfo.modules.<module key>` locale keys (tagline, features[], fits).
const getDetails = (t, i18n, key) => {
  const base = `moduleInfo.modules.${key}`;
  if (!i18n.exists(`${base}.tagline`)) return {};
  const features = t(`${base}.features`, { returnObjects: true });
  return {
    tagline: t(`${base}.tagline`),
    features: Array.isArray(features) ? features : null,
    fits: t(`${base}.fits`),
  };
};

const ModulesInfoPage = () => {
  const { t, i18n } = useTranslation();

  const [pricing, setPricing] = useState(null);
  const [cycle, setCycle] = useState('monthly');
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    // 8s timeout — server normally answers in <500ms, so anything
    // beyond that is a stuck request the user should be able to retry.
    axios.get(`${API}/api/billing/plan-pricing`, { timeout: 8000 })
      .then(({ data }) => { if (!cancelled) setPricing(data); })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.message || '');
        }
      });
    return () => { cancelled = true; };
  }, [reloadTick]);

  if (error !== null && !pricing) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow p-6 text-center">
          <div className="text-3xl mb-2">📡</div>
          <h2 className="text-lg font-semibold text-gray-900 mb-1">
            {t('moduleInfo.catalog.loadErrorTitle')}
          </h2>
          <p className="text-sm text-gray-500 mb-4">{error || t('moduleInfo.catalog.loadErrorFallback')}</p>
          <button onClick={() => setReloadTick((n) => n + 1)}
                  className="btn-primary">
            {t('moduleInfo.catalog.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!pricing) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        <div className="text-center">
          <div className="w-10 h-10 mx-auto mb-3 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          <div>{t('moduleInfo.catalog.loading')}</div>
        </div>
      </div>
    );
  }

  const recMap = pricing.recommended_with || {};
  const reqMap = pricing.module_requires || {};
  const moduleByKey = Object.fromEntries((pricing.available_modules || []).map((m) => [m.key, m]));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <header className="text-center mb-10">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-3">{t('moduleInfo.catalog.title')}</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('moduleInfo.catalog.subtitle')}</p>
          <div className="inline-flex bg-white rounded-full p-1 shadow mt-6">
            {['monthly', 'yearly'].map((v) => (
              <button key={v} onClick={() => setCycle(v)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition ${cycle === v ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                {v === 'yearly'
                  ? (pricing.yearly_discount_pct
                    ? t('moduleInfo.catalog.yearlyToggleDiscount', { pct: pricing.yearly_discount_pct })
                    : t('moduleInfo.catalog.yearlyToggle'))
                  : t('moduleInfo.catalog.monthlyToggle')}
              </button>
            ))}
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {(pricing.available_modules || []).map((m) => {
            const det = getDetails(t, i18n, m.key);
            const price = cycle === 'yearly' ? m.price_yearly : m.price_monthly;
            const fmtPrice = formatCurrency(Number(price || 0), 'TRY', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
            const requires = reqMap[m.key] || [];
            const recommended = (recMap[m.key] || []).filter((k) => !requires.includes(k));
            return (
              <div key={m.key} className="bg-white rounded-xl shadow-md p-6 flex flex-col hover:shadow-lg transition">
                <div className="flex items-start gap-3 mb-3">
                  <div className="text-3xl">{m.icon || '📦'}</div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-bold text-gray-900">{moduleDisplayName(m, t)}</h2>
                    <div className="text-blue-600 font-semibold text-sm mt-0.5">
                      {fmtPrice} <span className="text-gray-500 text-xs">{cycle === 'yearly' ? t('moduleInfo.catalog.perYear') : t('moduleInfo.catalog.perMonth')}</span>
                    </div>
                  </div>
                </div>
                {det.tagline && <p className="text-sm text-gray-600 mb-4 italic">{det.tagline}</p>}
                {det.features && (
                  <div className="mb-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('moduleInfo.catalog.featuresLabel')}</div>
                    <ul className="text-sm text-gray-700 space-y-1">
                      {det.features.map((f) => (
                        <li key={f} className="flex items-start gap-1.5">
                          <span className="text-green-600 flex-shrink-0">✓</span>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {det.fits && (
                  <div className="mb-3 text-xs">
                    <span className="font-semibold text-gray-500 uppercase tracking-wide">{t('moduleInfo.catalog.fitsLabel')}</span>{' '}
                    <span className="text-gray-700">{det.fits}</span>
                  </div>
                )}
                {requires.length > 0 && (
                  <div className="mt-auto pt-3 border-t text-xs">
                    <span className="font-semibold text-amber-700">{t('moduleInfo.catalog.requiresLabel')}</span>{' '}
                    {requires.map((rk, i) => (
                      <span key={rk}>
                        {i > 0 && ', '}
                        <b>{moduleDisplayName(moduleByKey[rk] || { key: rk }, t)}</b>
                      </span>
                    ))}
                  </div>
                )}
                {recommended.length > 0 && (
                  <div className="text-xs mt-2">
                    <span className="font-semibold text-gray-500">{t('moduleInfo.catalog.recommendedLabel')}</span>{' '}
                    {recommended.map((rk, i) => (
                      <span key={rk} className="text-gray-700">
                        {i > 0 && ', '}
                        {moduleDisplayName(moduleByKey[rk] || { key: rk }, t)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="text-center mt-12 space-y-3">
          <Link to="/signup"
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-lg font-semibold text-lg shadow-lg transition">
            {t('moduleInfo.catalog.cta')} →
          </Link>
          <div>
            <Link to="/" className="text-sm text-gray-600 hover:text-gray-900 hover:underline">
              ← {t('moduleInfo.catalog.backToPricing')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModulesInfoPage;
